import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { LahanPercontohanDataType } from './types'
import type { MutationLahanPercontohanValues } from './validations'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['lahan-percontohan', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<LahanPercontohanDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<LahanPercontohanDataType>>
      >(`kelembagaan/bpp/${params?.id_bpp}/lahan-percontohan`, {
        params: {
          page: params?.page,
          per_page: params?.perPage || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string,
  id_bpp?: string
): ServiceQueryOptionType<
  ServiceGetByIdResponse<LahanPercontohanDataType>
> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<LahanPercontohanDataType>
      >(`kelembagaan/bpp/${id_bpp ?? ''}/lahan-percontohan/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LahanPercontohanDataType>,
  MutationLahanPercontohanValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationLahanPercontohanValues) => {
    try {
      const response = await api.post(
        `kelembagaan/bpp/${data.id_bpp ?? ''}/lahan-percontohan`,
        {
          ...data,
          id_lahan_percontohan: data.id_lahan_percontohan.value
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LahanPercontohanDataType>,
  MutationLahanPercontohanValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationLahanPercontohanValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<LahanPercontohanDataType>
      >(
        `kelembagaan/bpp/${data?.id_bpp ?? ''}/lahan-percontohan/${data.id_lahan}`,
        {
          ...data,
          id_lahan_percontohan: data.id_lahan_percontohan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LahanPercontohanDataType>,
  { id: string; id_bpp: string }
> => ({
  mutationFn: async (data: { id: string; id_bpp: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<LahanPercontohanDataType>
      >(`kelembagaan/bpp/${data?.id_bpp ?? ''}/lahan-percontohan/${data.id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const lahanPercontohan = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData
}
